import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import CustomModal from '../shared/CustomModal/CustomModal'
import {
  ALLOW_ALL_IP,
  entriesFromRow,
  formatAllowedIp,
  formatRoleLabel,
  GET_ROLE_IP_WHITELISTS,
  isUnrestrictedIps,
  UPDATE_ROLE_IP_WHITELIST,
} from '../../graphql/roleIpWhitelist'
import './RoleIpWhitelistList.css'

const IPV4_REGEX =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/

function fetchWithTimeout(url, ms = 6000) {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), ms)
  return fetch(url, { signal: controller.signal }).finally(() => {
    window.clearTimeout(timer)
  })
}

async function detectPublicIp() {
  const attempts = [
    async () => {
      const res = await fetchWithTimeout('https://api.ipify.org?format=json')
      if (!res.ok) throw new Error('ipify failed')
      const data = await res.json()
      return String(data?.ip || '').trim()
    },
    async () => {
      const res = await fetchWithTimeout('https://ifconfig.me/ip')
      if (!res.ok) throw new Error('ifconfig failed')
      return String(await res.text()).trim()
    },
  ]

  for (const attempt of attempts) {
    try {
      const ip = await attempt()
      if (IPV4_REGEX.test(ip)) return ip
    } catch {
      // try next provider
    }
  }
  throw new Error('No se pudo detectar tu IP pública')
}

function GlobeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  )
}

const MAX_IPS = 20
const MAX_DESCRIPTION = 80
const OPEN_ENTRY = { ip: ALLOW_ALL_IP, description: '' }

function cleanDescription(value) {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_DESCRIPTION)
}

function asEntries(list) {
  return (list || [])
    .map((item) => (
      typeof item === 'string'
        ? { ip: item, description: '' }
        : { ip: item?.ip || '', description: item?.description || '' }
    ))
    .filter((item) => item.ip)
}

function withIp(list, ip, description = '') {
  const value = String(ip || '').trim()
  if (!IPV4_REGEX.test(value) || value === ALLOW_ALL_IP) {
    return {
      error: value === ALLOW_ALL_IP
        ? 'Para dejar el rol sin restricción usa “Permitir todas”.'
        : 'Ingresa una IPv4 válida.',
    }
  }
  const next = asEntries(list).filter((item) => item.ip !== ALLOW_ALL_IP)
  if (next.some((item) => item.ip === value)) return { error: 'Esa IP ya está en la lista.' }
  if (next.length >= MAX_IPS) return { error: `Máximo ${MAX_IPS} IPs por rol.` }
  return {
    entries: [...next, { ip: value, description: cleanDescription(description) }],
  }
}

function withoutIp(list, ip) {
  const next = asEntries(list).filter((item) => item.ip !== ip && item.ip !== ALLOW_ALL_IP)
  return next.length ? next : [OPEN_ENTRY]
}

function describeIp(list, ip, description) {
  const value = String(description || '').slice(0, MAX_DESCRIPTION)
  return asEntries(list).map((item) => (
    item.ip === ip ? { ...item, description: value } : item
  ))
}

function RestrictionBadge({ allowedIps }) {
  const open = isUnrestrictedIps(allowedIps)
  const count = asEntries(allowedIps).filter((item) => item.ip !== ALLOW_ALL_IP).length
  const label = open ? 'Sin restricción' : count === 1 ? 'IP fija' : `${count} IPs`
  return (
    <span className={`status-badge ${open ? 'activo' : 'desactivado'}`}>
      <span className="status-dot" />
      {label}
    </span>
  )
}

function RoleIpEditContent({
  editing,
  allowedIps,
  draft,
  setDraft,
  draftDescription,
  setDraftDescription,
  onAddDraft,
  onRemove,
  onDescribe,
  currentIp,
  currentIpLoading,
  currentIpError,
  onUseCurrentIp,
  onAllowAll,
}) {
  const roleLabel = formatRoleLabel(editing?.role)
  const saved = entriesFromRow(editing)
  const open = isUnrestrictedIps(allowedIps)

  return (
    <div className="role-ip-modal-body">
      <div className="role-ip-modal-icon" aria-hidden>
        <GlobeIcon />
      </div>

      <p className="role-ip-modal-lead">
        Define las IPs públicas desde las que <strong>{roleLabel}</strong> puede
        acceder al portal.
      </p>

      <div className="role-ip-modal-summary">
        <div className="role-ip-modal-summary-type">{roleLabel}</div>
        <div className="role-ip-modal-summary-row">
          <span className="role-ip-modal-summary-label">Rol</span>
          <span className="role-ip-modal-summary-value">{editing?.role}</span>
        </div>
        <div className="role-ip-modal-summary-row">
          <span className="role-ip-modal-summary-label">Guardadas</span>
          <span className="role-ip-modal-summary-value">
            {isUnrestrictedIps(saved)
              ? formatAllowedIp(ALLOW_ALL_IP)
              : saved.map((entry) => entry.ip).join(', ')}
          </span>
        </div>
      </div>

      <div className="role-ip-chip-list">
        {open ? (
          <div className="role-ip-chip">
            <span>{formatAllowedIp(ALLOW_ALL_IP)}</span>
          </div>
        ) : (
          allowedIps.map((entry) => (
            <div className="role-ip-chip" key={entry.ip}>
              <div className="role-ip-chip-main">
                <span className="role-ip-code">{entry.ip}</span>
                <input
                  className="role-ip-chip-description"
                  type="text"
                  value={entry.description || ''}
                  onChange={(event) => onDescribe(entry.ip, event.target.value)}
                  placeholder="Descripción (oficina, casa…)"
                  maxLength={MAX_DESCRIPTION}
                  aria-label={`Descripción de ${entry.ip}`}
                />
              </div>
              <button type="button" className="role-ip-chip-remove" onClick={() => onRemove(entry.ip)}>
                Quitar
              </button>
            </div>
          ))
        )}
      </div>

      <div className="role-ip-modal-field">
        <label htmlFor="role-ip-input">Agregar IP</label>
        <div className="role-ip-add-row">
          <input
            id="role-ip-input"
            type="text"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                onAddDraft()
              }
            }}
            placeholder="190.0.0.1"
            spellCheck={false}
            autoComplete="off"
          />
          <input
            id="role-ip-description"
            type="text"
            value={draftDescription}
            onChange={(event) => setDraftDescription(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                onAddDraft()
              }
            }}
            placeholder="Descripción"
            maxLength={MAX_DESCRIPTION}
            aria-label="Descripción de la IP"
          />
          <button type="button" className="role-ip-add-btn" onClick={onAddDraft}>
            Agregar
          </button>
        </div>
      </div>

      <div className="role-ip-modal-actions-row">
        <button
          type="button"
          className="role-ip-current-btn"
          onClick={onUseCurrentIp}
          disabled={currentIpLoading}
        >
          {currentIpLoading
            ? 'Detectando IP…'
            : currentIp
              ? `Agregar mi IP actual (${currentIp})`
              : 'Agregar mi IP actual'}
        </button>
        <button type="button" className="role-ip-allow-all-btn" onClick={onAllowAll}>
          Permitir todas (0.0.0.0)
        </button>
      </div>

      {currentIpError ? (
        <p className="role-ip-modal-error">{currentIpError}</p>
      ) : null}

      <p className="role-ip-modal-hint">
        La descripción es una nota para reconocer la IP. Con Tailscale + exit{' '}
        <strong>oficina-chile</strong>, tu IP debería ser la de egreso corporativo.{' '}
        <strong>0.0.0.0</strong> deja el rol sin restricción. Si el usuario tiene varios
        roles, uno con IPs fijas manda sobre los abiertos: basta con coincidir con alguna.
      </p>
    </div>
  )
}

export function RoleIpWhitelistList() {
  const [result, reexecuteQuery] = useQuery({ query: GET_ROLE_IP_WHITELISTS })
  const [, updateWhitelist] = useMutation(UPDATE_ROLE_IP_WHITELIST)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [allowedIps, setAllowedIps] = useState([OPEN_ENTRY])
  const [draft, setDraft] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState(null)

  const [currentIp, setCurrentIp] = useState('')
  const [currentIpLoading, setCurrentIpLoading] = useState(false)
  const [currentIpError, setCurrentIpError] = useState('')

  const rows = useMemo(
    () => result.data?.roleIpWhitelists || [],
    [result.data],
  )

  const showFeedback = (type, message) => {
    setFeedback({ type, message })
    window.setTimeout(() => setFeedback(null), 4000)
  }

  const loadCurrentIp = useCallback(async ({ silent = false } = {}) => {
    setCurrentIpLoading(true)
    setCurrentIpError('')
    try {
      const ip = await detectPublicIp()
      setCurrentIp(ip)
      return ip
    } catch (error) {
      const message = error?.message || 'No se pudo detectar tu IP pública'
      if (!silent) setCurrentIpError(message)
      return null
    } finally {
      setCurrentIpLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!modalOpen) return undefined
    loadCurrentIp({ silent: true })
    return undefined
  }, [modalOpen, loadCurrentIp])

  const openEdit = (row) => {
    setEditing(row)
    setAllowedIps(entriesFromRow(row))
    setDraft('')
    setDraftDescription('')
    setCurrentIpError('')
    setModalOpen(true)
  }

  const closeModal = ({ force = false } = {}) => {
    if (saving && !force) return
    setModalOpen(false)
    setEditing(null)
    setAllowedIps([OPEN_ENTRY])
    setDraft('')
    setDraftDescription('')
    setCurrentIpError('')
  }

  const applyIp = (ip, description = draftDescription) => {
    const result = withIp(allowedIps, ip, description)
    if (result.error) {
      setCurrentIpError(result.error)
      return false
    }
    setAllowedIps(result.entries)
    setDraft('')
    setDraftDescription('')
    setCurrentIpError('')
    return true
  }

  const handleUseCurrentIp = async () => {
    const ip = currentIp || (await loadCurrentIp())
    if (ip) applyIp(ip)
  }

  const savedMessage = (role, ips) => {
    const label = formatRoleLabel(role)
    if (isUnrestrictedIps(ips)) return `${label}: sin restricción de IP.`
    const addresses = ips.map((entry) => entry.ip || entry)
    if (addresses.length === 1) return `${label}: solo desde ${addresses[0]}.`
    return `${label}: desde ${addresses.join(', ')}.`
  }

  const handleSave = async () => {
    let ips = allowedIps
    const pending = String(draft || '').trim()
    if (pending) {
      const result = withIp(ips, pending, draftDescription)
      if (result.error) {
        setCurrentIpError(result.error)
        return
      }
      ips = result.entries
    }
    if (!ips.length || !editing?.id) return

    setSaving(true)
    try {
      const res = await updateWhitelist({
        input: {
          id: editing.id,
          allowedIps: ips.map((entry) => {
            const description = cleanDescription(entry.description)
            return description ? { ip: entry.ip, description } : { ip: entry.ip }
          }),
        },
      })
      if (res.error) throw res.error
      showFeedback('success', savedMessage(editing.role, ips))
      closeModal({ force: true })
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      setCurrentIpError(error?.message || 'No se pudieron guardar las IPs.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="role-ip-page">
      <div className="role-ip-page__header">
        <div>
          <h1>Whitelist IP por rol</h1>
          <p>
            Controla desde qué redes públicas entran al portal los roles internos.
            Cada rol puede tener una o más IPs. Por defecto <strong>0.0.0.0</strong> = todas.
          </p>
        </div>
      </div>

      {feedback ? (
        <div className={`role-ip-feedback role-ip-feedback--${feedback.type}`}>
          {feedback.message}
        </div>
      ) : null}

      {result.error ? (
        <div className="role-ip-feedback role-ip-feedback--error">
          {result.error.message || 'Error al cargar la whitelist.'}
        </div>
      ) : null}

      <div className="role-ip-table-wrap">
        <table className="role-ip-table">
          <thead>
            <tr>
              <th>Rol</th>
              <th>IPs permitidas</th>
              <th>Estado</th>
              <th>Actualizado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {result.fetching && !rows.length ? (
              <tr>
                <td colSpan={5}>Cargando…</td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr
                key={row.id}
                className="role-ip-row"
                onClick={() => openEdit(row)}
              >
                <td>
                  <strong>{formatRoleLabel(row.role)}</strong>
                  <div className="role-ip-muted">{row.role}</div>
                </td>
                <td>
                  <div className="role-ip-codes">
                    {isUnrestrictedIps(entriesFromRow(row)) ? (
                      <span className="role-ip-code">{formatAllowedIp(ALLOW_ALL_IP)}</span>
                    ) : (
                      entriesFromRow(row).map((entry) => (
                        <span className="role-ip-entry" key={entry.ip}>
                          <span className="role-ip-code">{entry.ip}</span>
                          {entry.description ? (
                            <span className="role-ip-entry-note">{entry.description}</span>
                          ) : null}
                        </span>
                      ))
                    )}
                  </div>
                </td>
                <td>
                  <RestrictionBadge allowedIps={entriesFromRow(row)} />
                </td>
                <td className="role-ip-muted">
                  {row.updatedAt
                    ? new Date(row.updatedAt).toLocaleString('es-CL')
                    : '—'}
                </td>
                <td>
                  <button
                    type="button"
                    className="role-ip-edit-btn"
                    onClick={(event) => {
                      event.stopPropagation()
                      openEdit(row)
                    }}
                  >
                    Configurar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CustomModal
        open={modalOpen}
        onClose={() => closeModal()}
        size="small"
        classNameModal="role-ip-modal"
        classNameContent="role-ip-modal-content"
        title={`Restringir acceso · ${formatRoleLabel(editing?.role)}`}
        content={(
          <RoleIpEditContent
            editing={editing}
            allowedIps={allowedIps}
            draft={draft}
            setDraft={setDraft}
            draftDescription={draftDescription}
            setDraftDescription={setDraftDescription}
            onAddDraft={() => applyIp(draft, draftDescription)}
            onRemove={(ip) => {
              setAllowedIps(withoutIp(allowedIps, ip))
              setCurrentIpError('')
            }}
            onDescribe={(ip, description) => {
              setAllowedIps(describeIp(allowedIps, ip, description))
            }}
            currentIp={currentIp}
            currentIpLoading={currentIpLoading}
            currentIpError={currentIpError}
            onUseCurrentIp={handleUseCurrentIp}
            onAllowAll={() => {
              setAllowedIps([OPEN_ENTRY])
              setDraft('')
              setDraftDescription('')
              setCurrentIpError('')
            }}
          />
        )}
        onConfirm={handleSave}
        confirmLabel={saving ? 'Guardando…' : 'Guardar IPs'}
        confirmDisabled={saving || currentIpLoading}
        isLoading={saving}
        closeOnDimmerClick={!saving}
        closeOnEscape={!saving}
      />
    </div>
  )
}

export default RoleIpWhitelistList
